import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

const PRODUCT_CARD_IMAGE_SELECTOR = '.product-media__image';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {WeakMap<HTMLImageElement, string>} */
  #defaultImageSizes = new WeakMap();

  #gridObserver = new MutationObserver(() => this.#syncProductCardImageSizes());

  #featureRevision = 0;
  #resultsConnected = false;

  connectedCallback() {
    if (this.querySelector('[data-feature-pagination]')) {
      this.#prepareFeature().then((current) => {
        if (current) this.#connectResults();
      });
      return;
    }
    this.#connectResults();
  }

  #connectResults() {
    if (this.#resultsConnected || !this.isConnected) return;
    this.#resultsConnected = true;
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    document.addEventListener('facets:section-rendered', this.#handleFeatureRender);
    this.#observeGrid();
    this.setAttribute('initialized', '');
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeGrid();
    if (this.querySelector('[data-feature-pagination]')) {
      this.#prepareFeature().then((current) => {
        if (current) {
          this.#connectResults();
          this.#observeGrid();
        }
      });
    }
  }

  async #prepareFeature() {
    const revision = ++this.#featureRevision;
    try {
      const { prepareFeaturePage } = await import(this.querySelector('[data-feature-pagination]').dataset.featureModule);
      await prepareFeaturePage(this, new URL(window.location.href), () => this.isConnected && revision === this.#featureRevision);
    } catch (error) {
      // Keep the complete native product grid working if the enhancement cannot load.
      console.error('Could not prepare the collection feature', error);
    }
    return this.isConnected && revision === this.#featureRevision;
  }

  async preparePageHTML(html, url) {
    const metadata = this.querySelector('[data-feature-pagination]');
    if (!metadata || !html.includes('data-feature-pagination')) return html;
    const { prepareFeaturePageHTML } = await import(metadata.dataset.featureModule);
    return prepareFeaturePageHTML(html, url);
  }

  // The collection filter event runs after the live subtree has finished morphing.
  #handleFeatureRender = () => {
    this.#featureRevision++;
    if (!this.querySelector('[data-feature-pagination]')) return;
    this.#prepareFeature().then((current) => {
      if (!current) return;
      super.updatedCallback();
      this.#observeGrid();
      this.setAttribute('initialized', '');
    });
  };

  async handlePageLoadError(error, url, retry) {
    if (!this.querySelector('[data-feature-pagination]')) return super.handlePageLoadError(error, url, retry);
    const { showFeaturePageError } = await import(this.querySelector('[data-feature-pagination]').dataset.featureModule);
    showFeaturePageError(this, url, async () => {
      if (await retry()) this.querySelector('[data-feature-error]')?.remove();
    });
  }

  disconnectedCallback() {
    this.#featureRevision++;
    this.#resultsConnected = false;
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    document.removeEventListener('facets:section-rendered', this.#handleFeatureRender);
    this.#gridObserver.disconnect();
    this.#defaultImageSizes = new WeakMap();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;

    grid.setAttribute('product-grid-view', value);
    this.#syncProductCardImageSizes();
  }

  #observeGrid() {
    this.#gridObserver.disconnect();

    const { grid } = this.refs;
    if (!grid) return;

    this.#gridObserver.observe(grid, { childList: true, subtree: true });
    this.#syncProductCardImageSizes();
  }

  #syncProductCardImageSizes = () => {
    const { grid } = this.refs;
    if (!grid) return;

    const useSingleColumnSize = !mediaQueryLarge.matches && grid.getAttribute('product-grid-view') === 'mobile-single';
    const images = /** @type {NodeListOf<HTMLImageElement>} */ (grid.querySelectorAll(PRODUCT_CARD_IMAGE_SELECTOR));

    for (const image of images) {
      if (!this.#defaultImageSizes.has(image)) this.#defaultImageSizes.set(image, image.sizes);

      const sizes = useSingleColumnSize ? '100vw' : this.#defaultImageSizes.get(image);
      if (sizes !== undefined && image.sizes !== sizes) image.sizes = sizes;
    }
  };

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
